/**
 * Enhanced New Transaction Wizard Component
 * 3-step wizard with animated transitions for creating raw material transactions
 *
 * @description
 * This wizard guides users through creating a new transaction with smooth animations:
 * Step 1: Source Selection (Gaushala + Material Type)
 * Step 2: Quantity Entry
 * Step 3: Confirmation
 *
 * Features:
 * - Animated modal with spring physics
 * - Smooth step transitions with slide/fade effects
 * - Form validation with zod
 * - Loading states with animated spinner
 * - Success/error toast notifications
 * - Auto-close on success
 * - Prevents closing during submission
 *
 * @author SAUBHAGYA Development Team
 * @since Story 2-5 (Enhanced in Epic 6)
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Building2,
  Package,
  Scale,
  FileText,
  Sparkles,
  X,
  Search,
  ChevronsUpDown,
  Check,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import { useMasterData } from '@/hooks/useMasterData';
import { useCreateTransaction } from '@/hooks/useTransactions';
import { useGaushalas } from '@/hooks/useGaushalas';

// ============================================
// TYPES & SCHEMA
// ============================================

const transactionSchema = z.object({
  gaushalaId: z
    .number({
      required_error: 'Please select a gaushala',
    })
    .min(1, 'Please select a gaushala'),
  rawMaterialTypeId: z
    .number({
      required_error: 'Please select a raw material type',
    })
    .min(1, 'Please select a raw material type'),
  dungTypeId: z.number().optional(),
  quantityKg: z
    .number({
      required_error: 'Please enter quantity',
    })
    .positive('Quantity must be greater than 0')
    .multipleOf(0.01, 'Maximum 2 decimal places'),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
});

type TransactionFormData = z.infer<typeof transactionSchema>;

interface NewTransactionWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

// ============================================
// ANIMATION VARIANTS
// ============================================

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.2 },
  },
};

const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 50 : -50,
    opacity: 0,
  }),
};

const stepTransition = {
  x: { type: 'spring', stiffness: 300, damping: 30 },
  opacity: { duration: 0.2 },
};

// ============================================
// STEP CONFIGURATION
// ============================================

const steps = [
  {
    number: 1,
    title: 'Source Selection',
    description: 'Select gaushala and material type',
    icon: Building2,
  },
  {
    number: 2,
    title: 'Quantity Entry',
    description: 'Enter quantity and notes',
    icon: Scale,
  },
  {
    number: 3,
    title: 'Confirmation',
    description: 'Review and confirm',
    icon: CheckCircle2,
  },
];

// ============================================
// COMPONENT
// ============================================

export function NewTransactionWizard({
  open,
  onOpenChange,
  onSuccess,
}: NewTransactionWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(0);
  const [gaushalaPopoverOpen, setGaushalaPopoverOpen] = useState(false);
  const [gaushalaSearch, setGaushalaSearch] = useState('');
  const gaushalaScrollRef = useRef<HTMLDivElement>(null);
  const { rawMaterialTypes, dungTypes, isLoading: masterDataLoading } = useMasterData();
  const { data: gaushalas, isLoading: gaushalasLoading } = useGaushalas();
  const createTransaction = useCreateTransaction();

  // Handle wheel scrolling for gaushala dropdown - More robust solution
  // Uses requestAnimationFrame to wait for the Popover Portal to mount the ref
  useEffect(() => {
    if (!gaushalaPopoverOpen) return;

    const rafId = requestAnimationFrame(() => {
      const scrollContainer = gaushalaScrollRef.current;
      if (!scrollContainer) return;

      console.log('[GaushalaDropdown] Wheel scroll listener attached');

      const handleWheel = (e: WheelEvent) => {
        const el = gaushalaScrollRef.current;
        if (!el) return;

        // Check if the mouse is over the scroll container
        const rect = el.getBoundingClientRect();
        const isMouseOver =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;

        if (!isMouseOver) return;

        const { scrollTop, scrollHeight, clientHeight } = el;
        const canScrollUp = scrollTop > 0 && e.deltaY < 0;
        const canScrollDown = scrollTop + clientHeight < scrollHeight && e.deltaY > 0;

        if (canScrollUp || canScrollDown) {
          e.preventDefault();
          e.stopPropagation();
          el.scrollTop += e.deltaY;
        }
      };

      document.addEventListener('wheel', handleWheel, { passive: false, capture: true });

      // Store cleanup for when the effect re-runs
      scrollContainer.dataset.wheelCleanup = 'true';
      const cleanup = () => {
        document.removeEventListener('wheel', handleWheel, { capture: true });
      };
      // Attach cleanup to the ref so we can call it on unmount
      (gaushalaScrollRef as any)._wheelCleanup = cleanup;
    });

    return () => {
      cancelAnimationFrame(rafId);
      if ((gaushalaScrollRef as any)._wheelCleanup) {
        (gaushalaScrollRef as any)._wheelCleanup();
        (gaushalaScrollRef as any)._wheelCleanup = null;
      }
    };
  }, [gaushalaPopoverOpen]);

  // Sort gaushalas alphabetically by name (A-Z)
  const sortedGaushalas = useMemo(() => {
    if (!gaushalas) return [];
    return [...gaushalas].sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
  }, [gaushalas]);

  // Filter gaushalas based on search
  const filteredGaushalas = useMemo(() => {
    if (!gaushalaSearch.trim()) return sortedGaushalas;
    const searchLower = gaushalaSearch.toLowerCase();
    return sortedGaushalas.filter(
      (g) =>
        g.name.toLowerCase().includes(searchLower) ||
        g.location.toLowerCase().includes(searchLower) ||
        (g.district && g.district.toLowerCase().includes(searchLower)) ||
        (g.area && g.area.toLowerCase().includes(searchLower))
    );
  }, [sortedGaushalas, gaushalaSearch]);

  const form = useForm<TransactionFormData>({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      gaushalaId: undefined as unknown as number,
      rawMaterialTypeId: undefined as unknown as number,
      quantityKg: 0,
      notes: '',
    },
    mode: 'onChange',
  });

  const isSubmitting = createTransaction.isPending;

  // ============================================
  // STEP VALIDATION
  // ============================================

  const validateStep1 = useCallback(async () => {
    const isValid = await form.trigger(['gaushalaId', 'rawMaterialTypeId']);
    return isValid;
  }, [form]);

  const validateStep2 = useCallback(async () => {
    const isValid = await form.trigger(['quantityKg', 'notes']);
    return isValid;
  }, [form]);

  // ============================================
  // NAVIGATION HANDLERS
  // ============================================

  const handleNext = useCallback(async () => {
    let isValid = false;

    if (currentStep === 1) {
      isValid = await validateStep1();
    } else if (currentStep === 2) {
      isValid = await validateStep2();
    }

    if (isValid && currentStep < 3) {
      setDirection(1);
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, validateStep1, validateStep2]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setDirection(-1);
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      form.reset();
      setCurrentStep(1);
      setDirection(0);
      onOpenChange(false);
    }
  }, [isSubmitting, form, onOpenChange]);

  // ============================================
  // FORM SUBMISSION
  // ============================================

  const onSubmit = useCallback(
    async (data: TransactionFormData) => {
      try {
        const result = await createTransaction.mutateAsync({
          gaushalaId: data.gaushalaId,
          rawMaterialTypeId: data.rawMaterialTypeId,
          dungTypeId: data.dungTypeId,
          quantityKg: data.quantityKg,
          notes: data.notes,
        });

        toast.success('Transaction Created Successfully!', {
          description: (
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              <span>Batch code: <strong>{result.batchCode}</strong></span>
            </div>
          ),
        });

        onSuccess?.();
        handleClose();
      } catch (error) {
        toast.error('Failed to Create Transaction', {
          description: error instanceof Error ? error.message : 'An error occurred',
        });
      }
    },
    [createTransaction, handleClose, onSuccess]
  );

  // ============================================
  // DATA HELPERS
  // ============================================

  const getGaushalaName = useCallback(
    (id: number) => {
      const gaushala = sortedGaushalas.find((g) => g.id === id);
      return gaushala ? `${gaushala.name} - ${gaushala.location}` : '';
    },
    [sortedGaushalas]
  );

  const getRawMaterialTypeName = useCallback(
    (id: number) => {
      const type = rawMaterialTypes?.find((t) => t.id === id);
      return type?.name || '';
    },
    [rawMaterialTypes]
  );

  const getDungTypeName = useCallback(
    (id: number | undefined) => {
      if (!id) return 'N/A';
      const type = dungTypes?.find((t) => t.id === id);
      return type?.name || 'N/A';
    },
    [dungTypes]
  );

  // ============================================
  // RENDER STEP INDICATOR
  // ============================================

  const renderStepIndicator = () => (
    <div className="relative mb-4 sm:mb-8 flex-shrink-0">
      {/* Progress line */}
      <div className="absolute top-5 left-0 right-0 h-0.5 bg-muted" />
      <motion.div
        className="absolute top-5 left-0 h-0.5 bg-primary"
        initial={{ width: '0%' }}
        animate={{ width: `${((currentStep - 1) / 2) * 100}%` }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      />

      {/* Step circles */}
      <div className="relative flex justify-between">
        {steps.map((step) => {
          const StepIcon = step.icon;
          const isActive = currentStep === step.number;
          const isCompleted = currentStep > step.number;

          return (
            <div key={step.number} className="flex flex-col items-center">
              <motion.div
                className={cn(
                  'relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors duration-300',
                  isActive && 'border-primary bg-primary text-primary-foreground',
                  isCompleted && 'border-primary bg-primary text-primary-foreground',
                  !isActive && !isCompleted && 'border-muted bg-background text-muted-foreground'
                )}
                initial={false}
                animate={{
                  scale: isActive ? 1.1 : 1,
                }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              >
                {isCompleted ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  >
                    <CheckCircle2 className="h-5 w-5" />
                  </motion.div>
                ) : (
                  <StepIcon className="h-5 w-5" />
                )}
              </motion.div>
              <div className="mt-2 text-center">
                <p
                  className={cn(
                    'text-xs font-medium transition-colors',
                    isActive || isCompleted ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {step.title}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ============================================
  // RENDER STEP 1: SOURCE SELECTION
  // ============================================

  const renderStep1 = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center gap-3 mb-2 sm:mb-4">
        <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-primary/10">
          <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-sm sm:text-base">Source Selection</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">Choose gaushala and material type</p>
        </div>
      </div>

      <FormField
        control={form.control}
        name="gaushalaId"
        render={({ field }) => {
          const selectedGaushala = sortedGaushalas.find((g) => g.id === field.value);
          return (
            <FormItem className="flex flex-col">
              <FormLabel className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Gaushala <span className="text-destructive">*</span>
              </FormLabel>
              <Popover
                open={gaushalaPopoverOpen}
                onOpenChange={(open) => {
                  setGaushalaPopoverOpen(open);
                  if (!open) setGaushalaSearch('');
                }}
              >
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={gaushalaPopoverOpen}
                      className={cn(
                        'h-10 sm:h-12 w-full justify-between font-normal',
                        !field.value && 'text-muted-foreground'
                      )}
                      disabled={gaushalasLoading}
                    >
                      {gaushalasLoading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading gaushalas...
                        </span>
                      ) : selectedGaushala ? (
                        <div className="flex flex-col items-start text-left truncate">
                          <span className="font-medium truncate max-w-[280px]">
                            {selectedGaushala.name}
                          </span>
                          <span className="text-xs text-muted-foreground truncate max-w-[280px]">
                            {selectedGaushala.location}
                          </span>
                        </div>
                      ) : (
                        'Select gaushala...'
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-[420px] p-0" align="start" sideOffset={4}>
                  {/* Search Input */}
                  <div className="flex items-center border-b px-3 py-2">
                    <Search className="h-4 w-4 shrink-0 text-muted-foreground mr-2" />
                    <Input
                      placeholder="Search gaushala..."
                      value={gaushalaSearch}
                      onChange={(e) => setGaushalaSearch(e.target.value)}
                      className="h-9 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0"
                    />
                    {gaushalaSearch && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => setGaushalaSearch('')}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>

                  {/* List Header */}
                  <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b bg-muted/30">
                    {gaushalaSearch
                      ? `Found ${filteredGaushalas.length} of ${sortedGaushalas.length} gaushalas`
                      : `All Gaushalas (${sortedGaushalas.length}) - A to Z`
                    }
                  </div>

                  {/* Gaushala List - Compact with visible scrollbar */}
                  <div
                    ref={gaushalaScrollRef}
                    className="relative h-[280px] overflow-y-scroll overscroll-contain pointer-events-auto"
                    style={{
                      pointerEvents: 'auto',
                    }}
                  >
                    <div className="p-1">
                      {filteredGaushalas.length === 0 ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                          No gaushala found.
                        </div>
                      ) : (
                        filteredGaushalas.map((gaushala) => (
                          <div
                            key={gaushala.id}
                            onClick={() => {
                              field.onChange(gaushala.id);
                              setGaushalaPopoverOpen(false);
                              setGaushalaSearch('');
                            }}
                            className={cn(
                              'flex items-center gap-2 py-2 px-2 rounded-md cursor-pointer transition-colors',
                              'hover:bg-accent hover:text-accent-foreground',
                              field.value === gaushala.id && 'bg-accent'
                            )}
                          >
                            <Check
                              className={cn(
                                'h-4 w-4 shrink-0 text-primary',
                                field.value === gaushala.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <div className="flex flex-col min-w-0 flex-1">
                              <span className="font-medium text-sm truncate">{gaushala.name}</span>
                              <span className="text-xs text-muted-foreground truncate">
                                {gaushala.location}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <FormDescription>
                Search or select from the list (A-Z)
              </FormDescription>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={form.control}
        name="rawMaterialTypeId"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Raw Material Type <span className="text-destructive">*</span>
            </FormLabel>
            <Select
              onValueChange={(value) => field.onChange(parseInt(value, 10))}
              value={field.value ? field.value.toString() : ''}
              disabled={masterDataLoading}
            >
              <FormControl>
                <SelectTrigger className="h-10 sm:h-12">
                  <SelectValue placeholder="Select raw material type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {rawMaterialTypes?.map((type) => (
                  <SelectItem key={type.id} value={type.id.toString()}>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs">
                        {type.code}
                      </Badge>
                      <span>{type.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription>Select the type of raw material</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="dungTypeId"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2 text-muted-foreground">
              <Package className="h-4 w-4" />
              Dung Type
              <Badge variant="secondary" className="text-xs">
                Optional
              </Badge>
            </FormLabel>
            <Select
              onValueChange={(value) =>
                field.onChange(value && value !== 'none' ? parseInt(value, 10) : undefined)
              }
              value={field.value ? field.value.toString() : 'none'}
              disabled={masterDataLoading}
            >
              <FormControl>
                <SelectTrigger className="h-10 sm:h-12">
                  <SelectValue placeholder="Select dung type (optional)" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {dungTypes?.map((type) => (
                  <SelectItem key={type.id} value={type.id.toString()}>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs">
                        {type.code}
                      </Badge>
                      <span>{type.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription>Optionally specify the dung type</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );

  // ============================================
  // RENDER STEP 2: QUANTITY ENTRY
  // ============================================

  const renderStep2 = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center gap-3 mb-2 sm:mb-4">
        <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-primary/10">
          <Scale className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-sm sm:text-base">Quantity Entry</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">Enter quantity and additional details</p>
        </div>
      </div>

      <FormField
        control={form.control}
        name="quantityKg"
        render={({ field }) => {
          const [isFocused, setIsFocused] = useState(false);

          return (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Scale className="h-4 w-4" />
                Quantity (KG) <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    {...field}
                    value={isFocused && field.value === 0 ? '' : field.value || 0}
                    onFocus={(e) => {
                      setIsFocused(true);
                      // Select all text if there's any
                      if (field.value !== 0) {
                        e.target.select();
                      }
                    }}
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      if (value === '' || value === '0') {
                        field.onChange(0);
                      } else {
                        field.onChange(parseFloat(value) || 0);
                      }
                    }}
                    onBlur={(e) => {
                      setIsFocused(false);
                      // Reset to 0 if empty
                      if (e.target.value === '' || e.target.value === '0') {
                        field.onChange(0);
                      }
                      field.onBlur();
                    }}
                    className="h-12 sm:h-14 text-xl sm:text-2xl font-bold pr-14 sm:pr-16 text-center"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                    <span className="text-lg font-medium text-muted-foreground">KG</span>
                  </div>
                </div>
              </FormControl>
              <FormDescription>Enter quantity with up to 2 decimal places</FormDescription>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Notes
              <Badge variant="secondary" className="text-xs">
                Optional
              </Badge>
            </FormLabel>
            <FormControl>
              <Textarea
                placeholder="Add any additional notes about this transaction..."
                className="resize-none min-h-[80px] sm:min-h-[120px]"
                maxLength={500}
                {...field}
              />
            </FormControl>
            <div className="flex justify-between items-center">
              <FormDescription>Any relevant information about this batch</FormDescription>
              <span
                className={cn(
                  'text-xs',
                  (field.value?.length || 0) > 450 ? 'text-amber-500' : 'text-muted-foreground'
                )}
              >
                {field.value?.length || 0}/500
              </span>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <Card className="bg-muted/50 border-dashed">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-background">
                <Scale className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">Smart Scale Integration</p>
                <p className="text-xs text-muted-foreground">Coming soon</p>
              </div>
            </div>
            <Badge variant="outline" className="text-xs">
              Manual Entry
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ============================================
  // RENDER STEP 3: CONFIRMATION
  // ============================================

  const renderStep3 = () => {
    const formValues = form.getValues();

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center gap-3 mb-2 sm:mb-4">
          <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-emerald-500/10">
            <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
          </div>
          <div>
            <h3 className="font-semibold text-sm sm:text-base">Confirm Transaction</h3>
            <p className="text-xs sm:text-sm text-muted-foreground">Review details before submitting</p>
          </div>
        </div>

        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-4">
            <p className="text-sm font-medium text-muted-foreground">Transaction Summary</p>
          </div>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Gaushala
                </p>
                <p className="text-sm font-medium">{getGaushalaName(formValues.gaushalaId)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Raw Material Type
                </p>
                <p className="text-sm font-medium">
                  {getRawMaterialTypeName(formValues.rawMaterialTypeId)}
                </p>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Dung Type
                </p>
                <p className="text-sm font-medium">{getDungTypeName(formValues.dungTypeId)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Quantity
                </p>
                <p className="text-2xl font-bold text-primary">
                  {formValues.quantityKg.toFixed(2)}{' '}
                  <span className="text-sm font-normal text-muted-foreground">KG</span>
                </p>
              </div>
            </div>

            {formValues.notes && (
              <>
                <Separator />
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Notes
                  </p>
                  <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                    {formValues.notes}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center gap-2 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <Sparkles className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            A unique batch code will be generated upon creation
          </p>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER FOOTER
  // ============================================

  const renderFooter = () => (
    <div className="flex justify-between pt-6 border-t mt-auto flex-shrink-0 bg-background sticky bottom-0">
      <Button
        type="button"
        variant="ghost"
        onClick={handleBack}
        disabled={currentStep === 1 || isSubmitting}
        className="gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      {currentStep < 3 ? (
        <Button type="button" onClick={handleNext} className="gap-2">
          Next
          <ArrowRight className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          type="submit"
          disabled={isSubmitting}
          onClick={form.handleSubmit(onSubmit)}
          className="gap-2 min-w-[160px]"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4" />
              Create Transaction
            </>
          )}
        </Button>
      )}
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-[640px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
        onInteractOutside={(e) => {
          if (isSubmitting) {
            e.preventDefault();
          }
        }}
      >
        {/* Header - Fixed at top */}
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Package className="h-5 w-5 text-primary" />
            New Transaction
          </DialogTitle>
          <DialogDescription>
            {steps[currentStep - 1].description}
          </DialogDescription>
        </DialogHeader>

        {/* Content - Scrollable area */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
          {renderStepIndicator()}

          <Form {...form}>
            <form className="flex flex-col h-full" onSubmit={(e) => e.preventDefault()}>
              <div className="flex-1 min-h-[300px] sm:min-h-[380px]">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={currentStep}
                    custom={direction}
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={stepTransition}
                  >
                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                  </motion.div>
                </AnimatePresence>
              </div>

              {renderFooter()}
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
